import React, { useRef, useState } from "react";
import {
  Column,
  FilterRow,
  HeaderFilter,
  Item,
  LoadPanel,
  Toolbar,
  DataGrid,
  Scrolling,
  Paging,
  Pager,
} from "devextreme-react/data-grid";
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import AddIcon from "@mui/icons-material/Add";
import { Button, Dialog, DialogActions, DialogContent } from "@mui/material";
import { useRecoilValue } from "recoil";

import { dataGridConfig } from "../../constants";
import { usersInEntitySelector } from "../../recoil/selectors/users.selectors";
import { useUsersActions } from "../../recoil/actions/users.actions";
import { Entity, FolderNode, UserPerSite } from "../../api-client-nswag/custompermission-client";
import { DialogBox } from "../CreateSites/DialogBox";
import AssignUserCustomPermission from "./AssignUserCustomPermission";
import { buttonDisabledSelector } from "../../recoil/selectors/legalEntities.selectors";
import { applyOverflowTooltipOnCellPrepared, renderTruncatedTextCell } from "./manageCustomPermissionsGridHelpers";

interface UserCustomPermissionsManagerProps {
  selectedLegalEntities?: Entity | Entity[] | undefined;
  isEntityUserlevel?: boolean;
  selectedFolderItem?: FolderNode | undefined;
}

type DatagridRefType = DataGrid;

export const userActionOptions = [
  {
    label: "Edit Folder Permissions",
    value: "edit-folder-permissions",
  },
  {
    label: "Remove User",
    value: "remove-user",
  },
];

const UserCustomPermissionsManager: React.FC<
  UserCustomPermissionsManagerProps
> = ({ selectedLegalEntities, isEntityUserlevel, selectedFolderItem }) => {
  const dataGridRef = useRef<DatagridRefType>(null);
  const { addUsersToEntity, removeUsersFromEntity } = useUsersActions()
  const usersInEntity = useRecoilValue(usersInEntitySelector)
  const isButtonDisabled = useRecoilValue(buttonDisabledSelector)
  const [selectedUsers, setSelectedUsers] = useState<UserPerSite[]>([]);
  const [showRemoveUserModal, setShowRemoveUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [showAddUserModel, setShowAddUserModel] = useState(false);

  const handleConfirmRemoveUser = () => {
    removeUsersFromEntity([selectedUser])
    setShowRemoveUserModal(false);
    setSelectedUser(null);
  };

  const handleAddUser = () => {
    setShowAddUserModel(true);
  }

  const renderAddUserButton = () => {
    return (
      <Button
        variant="outlined"
        onClick={handleAddUser}
        startIcon={<AddIcon fontSize="small" className="mcp-add-user-icon" />}
        className="mcp-outlined-action-button"
      >
        Add User
      </Button>
    );
  };

  const handleAddUserModalClose = () => {
    setShowAddUserModel(false);
  };

  const handleAddUserChanges = () => {
    addUsersToEntity(selectedUsers);
    setSelectedUser(null);
    setSelectedUsers([]);
    setShowAddUserModel(false);
  }

  const cellUserNameRender = (cellData: any) => {
    return (
      <span className="hyper-link-blue">
        {cellData?.data?.username}
      </span>
    );
  };

  const cellRenderGroup = (cellData: any) => {
    return <span>{cellData?.data?.userRole}</span>;
  };

  const cellRenderEmail = (cellData: any) => {
    return renderTruncatedTextCell(cellData?.data?.userEmail);
  };

  const handleDataGridCellPrepared = (e: any) => {
    applyOverflowTooltipOnCellPrepared(e);
  };

  const handleRemoveUser = (user: UserPerSite) => {
    setSelectedUser(user);
    setShowRemoveUserModal(true);
  }

  const cellRenderActions = (e: any) => {
    const rowData = e.data;

    const groupName = (rowData?.userGroup ?? rowData?.userRole ?? "").toLowerCase();
    const isProtected = ["content admin", "site admin"].some((role) =>
      groupName.includes(role)
    );

    return (
      <div>
        <RemoveCircleOutlineIcon
          fontSize="small"
          className={isProtected ? "mcp-action-icon mcp-action-icon--disabled" : "mcp-action-icon"}
          onClick={isProtected ? undefined : () => handleRemoveUser(rowData)}
        />

      </div>
    );
  };

  return (
    <div className="mcp-users-container">
      <div className="mcp-hq-page">
        <DataGrid
          height={`calc(100vh - 250px)`}
          {...dataGridConfig}
          dataSource={usersInEntity}
          noDataText={"No data"}
          allowColumnResizing={true}
          ref={dataGridRef}
          showBorders={true}
          showColumnLines={false}
          showRowLines={true}
          onCellPrepared={handleDataGridCellPrepared}
        >
          <Pager visible={false} />
          <Paging enabled={false} />
          <Scrolling mode="virtual" />
          <LoadPanel enabled={false} />
          <FilterRow visible={true} />
          <HeaderFilter visible={true} />
          <Toolbar>
            <Item location="after" render={renderAddUserButton} />
          </Toolbar>
          <Column
            alignment="left"
            dataField="username"
            caption={"Name"}
            cellRender={cellUserNameRender}
          ></Column>
          <Column
            alignment="left"
            dataField="userRole"
            caption={"Group"}
            cellRender={cellRenderGroup}
          ></Column>
          <Column
            cssClass="cell-text"
            alignment="left"
            dataField="userEmail"
            caption={"Email ID"}
            cellRender={cellRenderEmail}
          ></Column>
          <Column
            type="buttons"
            caption={"Actions"}
            cellRender={cellRenderActions}
          />
        </DataGrid>
      </div>
      <Dialog
        open={showRemoveUserModal}
        onClose={() => setShowRemoveUserModal(false)}
      >
        <DialogContent className="mcp-confirm-dialog-content">Are you sure you want to revoke this access?</DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setShowRemoveUserModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmRemoveUser}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      <DialogBox
        isDisplayModal={showAddUserModel}
        handleClose={handleAddUserModalClose}
        btnOpenText={"Save"}
        maxWidth={"sm"}
        handleContinue={handleAddUserChanges}
        title={`Add Users`}
        btnContinueDisable={isButtonDisabled || selectedUsers.length === 0}
      >
        <AssignUserCustomPermission
          selectedLegalEntities={selectedLegalEntities}
          selectedFolderItem={selectedFolderItem}
          selectedUsers={selectedUsers}
          setSelectedUsers={setSelectedUsers} />
      </DialogBox>
    </div>
  );
};

export default UserCustomPermissionsManager;
